import cn from 'classnames';
import { useRouter } from 'next/router';

import { useDictionary } from 'src/hooks/useDictionary';

import { formatPhone } from 'src/utils/formatPhone';

import { ImageData, LinkData } from 'src/types/components';

import ArrowForward from 'assets/icons/arrow-forward.svg';

import Image from '../Image';
import Link from '../Link';
import PatternBackground from '../PatternBackground';
import RichText from '../RichText';
import Text from '../Text';

import styles from './LocationCard.module.scss';

export type LocationCardProps = {
  id?: number | string;
  name: string;
  address?: string;
  phone?: string;
  fax?: string;
  image?: ImageData;
  link: LinkData;
  className?: string;
  excerpt?: string;
};

const LocationCard = (props: LocationCardProps): JSX.Element => {
  const { name, address, phone, fax, image, link, className } = props;
  const { phoneLabel, faxLabel } = useDictionary();
  const formattedPhone = formatPhone(phone || '');
  const router = useRouter();

  const url = link.url || link.href;
  const isExternalLink =
    link.target !== undefined ? link.target === '_blank' : url.startsWith('http');

  return (
    <div
      className={cn(styles.main, className)}
      onClick={(e) => {
        if ((e.target as HTMLElement)?.closest('a')) return;

        if (isExternalLink) {
          window.open(url, '_blank');
          return;
        }
        router.push(url);
      }}
    >
      {image ? (
        <>
          <Image {...image} className={cn(styles['border-radius'])} fill />
          <div className={cn(styles['image-overlay'], styles['border-radius'])} />
        </>
      ) : (
        <PatternBackground className={cn(styles['border-radius'])} />
      )}
      <div className={styles.content}>
        <Link href={url} className={cn('heading-h4')} openNewTab={isExternalLink}>
          <Text field={name} />
        </Link>
        {Boolean(address || phone || fax) && (
          <div className={styles['contact-information']}>
            <RichText
              field={address?.replace(/\r?\n/g, '<br />')}
              className={cn('body-sm')}
              styled={false}
            />
            {formattedPhone && (
              <div className={styles.row}>
                <Text className={cn(styles.label, 'body-eyebrow')} field={phoneLabel} />
                <Link href={formattedPhone.href} className={cn(styles['phone'], 'body-sm')}>
                  {formattedPhone.text}
                </Link>
              </div>
            )}
            {fax && (
              <div className={styles.row}>
                <Text className={cn(styles.label, 'body-eyebrow')} field={faxLabel} />
                <Text field={fax} className={cn('body-sm')} />
              </div>
            )}
            <RichText
              field={props.excerpt}
              className={cn(styles.excerpt, 'body-sm')}
              noDefaultBodyStyle={true}
            />
          </div>
        )}
      </div>
      <ArrowForward className={styles.arrow} />
    </div>
  );
};

export default LocationCard;
